import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Download, LoaderCircle, Search } from "lucide-react";
import { downloadReport, getReportMonths } from "../../utils/reportExports";

export default function ReportExportView({ title, initialMonth, loadUsers, onExport, onBack }) {
  const [start, setStart] = useState(initialMonth);
  const [end, setEnd] = useState(initialMonth);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(Boolean(loadUsers));
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const active = useRef(false);
  const inFlight = useRef(false);
  useEffect(() => {
    active.current = true;
    let cancelled = false;
    if (loadUsers) loadUsers().then(people => {
      if (cancelled) return;
      setUsers(people); setSelected(new Set(people.map(p => p.uid)));
    }).catch(() => { if (!cancelled) setLoadError("人員載入失敗，請返回後重試"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; active.current = false; };
  }, [loadUsers]);

  const submit = async e => {
    e.preventDefault();
    if (inFlight.current || loading || loadError) return;
    setError(""); setResult(null);
    try {
      getReportMonths(start, end);
      const selectedUsers = users.filter(u => selected.has(u.uid));
      if (loadUsers && !selectedUsers.length) throw new Error("請選擇至少一位人員");
      inFlight.current = true; setBusy(true);
      const report = await onExport({ start, end, selectedUsers });
      if (!active.current) return;
      downloadReport(report);
      setResult(report);
    } catch (err) {
      if (active.current) setError(err.code ? "讀取報表資料失敗，請確認權限與網路後重試" : err.message);
    } finally {
      inFlight.current = false;
      if (active.current) setBusy(false);
    }
  };
  const visibleUsers = users.filter(u => u.name.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="paper-page report-export-view p-4 pb-24">
      <div className="flex items-center gap-3 mb-5">
        <button type="button" onClick={onBack} title="返回" aria-label="返回" className="p-2 rounded bg-slate-100"><ArrowLeft size={18} /></button>
        <h2 className="text-lg font-bold">{title}</h2>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <fieldset disabled={busy} className="grid grid-cols-2 gap-3 min-w-0">
          <label className="text-xs font-bold min-w-0">開始月份<input aria-label="開始月份" type="month" value={start} onChange={e => { setStart(e.target.value); setResult(null); }} className="w-full p-2 mt-2 border rounded text-sm" required /></label>
          <label className="text-xs font-bold min-w-0">結束月份<input aria-label="結束月份" type="month" value={end} onChange={e => { setEnd(e.target.value); setResult(null); }} className="w-full p-2 mt-2 border rounded text-sm" required /></label>
        </fieldset>
        {loadUsers && <fieldset disabled={busy || loading || Boolean(loadError)} className="space-y-3 min-w-0">
          <legend className="font-bold text-sm mb-2">選擇人員</legend>
          <div className="flex items-center gap-2"><Search size={16} /><input aria-label="搜尋人員" placeholder="搜尋人員" value={search} onChange={e => setSearch(e.target.value)} className="w-full p-2 text-sm border rounded" /></div>
          <div className="flex justify-between items-center text-xs gap-2">
            <span>已選 {selected.size} / {users.length} 人</span>
            <div className="flex gap-3"><button type="button" onClick={() => { setSelected(new Set(users.map(u => u.uid))); setResult(null); }}>全選</button><button type="button" onClick={() => { setSelected(new Set()); setResult(null); }}>清除</button></div>
          </div>
          {loading ? <p role="status" className="text-sm">載入人員中…</p> : <div className="divide-y divide-slate-200 border rounded overflow-y-auto max-h-72">
            {visibleUsers.map(person => <label key={person.uid} className="flex items-center gap-3 p-3 cursor-pointer text-sm">
              <input type="checkbox" checked={selected.has(person.uid)} onChange={e => { const next = new Set(selected); e.target.checked ? next.add(person.uid) : next.delete(person.uid); setSelected(next); setResult(null); }} />
              <span className="min-w-0 break-words">{person.name}</span>
              {(person.isHidden || person.accountStatus === "archived") && <span className="text-xs text-slate-400 ml-auto shrink-0">離職／隱藏</span>}
            </label>)}
            {!visibleUsers.length && <p className="text-sm p-3 text-slate-500">沒有符合的人員</p>}
          </div>}
        </fieldset>}
        {(error || loadError) && <p role="alert" className="text-sm text-red-500">{error || loadError}</p>}
        {result && <div role="status" className="text-sm space-y-2"><p>{result.message}</p>{result.missing?.length > 0 && <details><summary className="cursor-pointer text-amber-600">{result.missing.length} 筆人員月份缺少資料（展開查看）</summary><ul className="mt-2 max-h-32 overflow-auto">{result.missing.map(item => <li key={item}>{item}</li>)}</ul></details>}</div>}
        <button type="submit" disabled={busy || loading || Boolean(loadError)} className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded py-3 font-bold disabled:opacity-50">
          {busy ? <LoaderCircle size={18} className="animate-spin" /> : <Download size={18} />}{busy ? "匯出中…" : "匯出"}
        </button>
      </form>
    </div>
  );
}
